import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "./ui/input";
import { Phone, Mail, MapPin, ArrowRight, Award } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import RedlineLogo from "./RedlineLogo";
import { trackNewsletterSignup, trackPhoneClick, trackEmailClick } from "../utils/analytics";

const SERVICES = ["Dedicated Virtual Guards", "Camera Installation", "License Plate Readers", "Reporting Dashboard", "Gate & Access Control"];
const LEGAL = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-of-service", label: "Terms of Service" },
  { to: "/hoa-partnership", label: "HOA Partnership" },
  { to: "/virtual-guard-standards", label: "Virtual Guard Standards" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubscribing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        await response.json();
        trackNewsletterSignup(email);
        toast({ title: "Subscribed", description: "You'll receive HOA virtual security best practices and updates.", duration: 3000 });
        setEmail("");
      } else {
        throw new Error('Failed to subscribe to newsletter');
      }
    } catch (error) {
      toast({ title: "Subscription Error", description: "There was a problem with your subscription. Please try again later.", variant: "destructive", duration: 3000 });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-[#0A0A0A] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Top: brand + newsletter */}
        <div className="grid lg:grid-cols-2 gap-12 py-16 border-b border-white/10">
          <div className="max-w-md">
            <RedlineLogo width={240} height={48} />
            <p className="text-white/55 leading-relaxed mt-6">
              Dedicated virtual guards exclusively for premium condominium & multi-family HOA communities —
              camera installation, license plate readers, and proactive monitoring, all handled for you.
            </p>
            <p className="text-white/40 text-sm mt-5">Serving Austin & Central Texas communities.</p>
          </div>

          <div className="lg:pl-12 lg:border-l border-white/10">
            <h3 className="font-head text-2xl font-semibold tracking-tight mb-3">Security best practices, monthly.</h3>
            <p className="text-white/55 mb-6">Insights on HOA community safety and dedicated-guard monitoring.</p>
            <form onSubmit={handleNewsletterSignup} className="flex gap-3" data-testid="newsletter-form">
              <Input
                type="email" placeholder="Enter your email" value={email}
                onChange={(e) => setEmail(e.target.value)} required data-testid="newsletter-email"
                className="flex-1 bg-white/5 border border-white/15 rounded-full text-white placeholder-white/40 px-5 py-3 focus:outline-none focus:border-[#D32F2F]"
              />
              <button type="submit" disabled={isSubscribing} data-testid="newsletter-submit"
                      className="inline-flex items-center gap-2 rounded-full bg-[#D32F2F] hover:bg-white hover:text-[#1A1A1A] text-white font-medium px-6 py-3 transition-colors disabled:opacity-60">
                {isSubscribing ? "…" : "Subscribe"}<ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Middle: links + contact */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 py-16">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40 mb-5">Service</h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s}><a href="#services" className="text-white/65 hover:text-[#D32F2F] transition-colors">{s}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40 mb-5">Company</h4>
            <ul className="space-y-3">
              {LEGAL.map((l) => (
                <li key={l.to}><Link to={l.to} className="text-white/65 hover:text-[#D32F2F] transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="sm:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40 mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#D32F2F]" />
                <a href="tel:+15123636584" onClick={() => trackPhoneClick('footer')} className="text-white font-semibold hover:text-[#D32F2F] transition-colors">(512) 363-6584</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#D32F2F]" />
                <a href="mailto:mike@redlinemonitoring.com" onClick={() => trackEmailClick('footer')} className="text-white font-semibold hover:text-[#D32F2F] transition-colors">mike@redlinemonitoring.com</a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#D32F2F]" />
                <span className="text-white/65">Austin, TX 78705 · Virtual Guard Command Center</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Premium positioning band */}
        <div className="py-10 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-head text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-snug max-w-xl">
            We protect a limited number of <span className="text-[#D32F2F]">premium communities.</span>
          </p>
          <button
            onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="footer-consultation-btn"
            className="group inline-flex items-center gap-3 self-start rounded-full bg-[#D32F2F] hover:bg-white hover:text-[#1A1A1A] text-white text-sm font-medium px-7 py-3.5 transition-colors whitespace-nowrap"
          >
            Request a Consultation
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Bottom bar */}
        <div className="py-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10">
          <p className="text-white/40 text-sm text-center sm:text-left">
            © 2026 Redline Monitoring Services Inc. · Premium Live Virtual Guard Service
          </p>
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <Award className="w-4 h-4 text-[#D32F2F]" />
            <span className="font-medium">Texas DPS License #B30959201</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
